import { useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import { VRCertificateTypes } from "@constants/certificates";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  FORM_FIELDS,
  generateCertificateField,
  isBunkerBargeInProgress as getIsBunkerBargeInProgress,
} from "@features/vessel-register/utils";
import { VESSEL_STATUS } from "@pages/vessel-management/vessel-status/VesselStatus";
import { getVesselTypes } from "@services/referenceDataApi";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";

const VesselParticulars = () => {
  const { formData, updateFormData, isRestrictedToCertificatesUpdate, vesselDetails } =
    useOutletContext<VesselOutletContext>();

  const isBunkerBargeAllowed = useMemo(
    () =>
      Boolean(
        vesselDetails?.vesselRegistrationStatus === VESSEL_STATUS.APPROVED ||
          vesselDetails?.bunkerRegistered,
      ),
    [vesselDetails?.vesselRegistrationStatus, vesselDetails?.bunkerRegistered],
  );

  const isBunkerBargeInProgress = getIsBunkerBargeInProgress(vesselDetails);

  const { data: fetchedVesselTypes = [] } = useQuery<
    { vesselTypeCode: string; vesselTypeDesc: string }[]
  >({
    queryKey: ["vessel-types"],
    queryFn: () =>
      getVesselTypes("bff-vessel") as Promise<{ vesselTypeCode: string; vesselTypeDesc: string }[]>,
  });

  const vesselTypeOptions = fetchedVesselTypes
    .map((vesselType) => ({
      label: vesselType.vesselTypeDesc.toUpperCase(),
      value: vesselType.vesselTypeCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const bunkerBargeCheckbox = formData.watch(FORM_FIELDS.BUNKER_REGISTERED);
  const diplomaticVesselCheckbox = formData.watch(FORM_FIELDS.DIPLOMATIC_VESSEL);

  return (
    <div data-testid="vessels-type-page" className="pb-8">
      <FormProvider {...formData}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.VESSEL_TYPE_CODE>
              testId="register-vessel-type-code-autocomplete"
              name={FORM_FIELDS.VESSEL_TYPE_CODE}
              label={t("vessel-management.vessel-type-code")}
              type="autocomplete"
              options={vesselTypeOptions}
              onFieldChange={(k, v) => {
                Object.values(VRCertificateTypes).forEach((certType) => {
                  formData.clearErrors(generateCertificateField.url(certType));
                  formData.clearErrors(generateCertificateField.expiryDate(certType));
                  formData.clearErrors(generateCertificateField.className(certType));
                });
                updateFormData(k, v);
              }}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1 lg:col-span-2 flex justify-start lg:justify-end">
            <div className="flex items-end gap-4">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DIPLOMATIC_VESSEL>
                  name={FORM_FIELDS.DIPLOMATIC_VESSEL}
                  type="checkbox"
                  label={t("vessel-management.diplomatic-vessel")}
                  testId="diplomatic-vessel-checkbox"
                  onFieldChange={(key, value) => {
                    updateFormData(key, value);
                    if (value) {
                      formData.clearErrors(FORM_FIELDS.DIPLOMATIC_VESSEL);
                    } else {
                      formData.trigger(FORM_FIELDS.DIPLOMATIC_VESSEL);
                    }
                  }}
                  defaultValue={diplomaticVesselCheckbox}
                  disabled={isRestrictedToCertificatesUpdate}
                />
              </div>
              {isBunkerBargeAllowed && (
                <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.BUNKER_REGISTERED>
                  name={FORM_FIELDS.BUNKER_REGISTERED}
                  type="checkbox"
                  label={t("vessel-management.bunker-barge")}
                  testId="bunker-barge-checkbox"
                  onFieldChange={(key, value) => {
                    updateFormData(key, value);
                    if (value) {
                      formData.clearErrors(FORM_FIELDS.BUNKER_REGISTERED);
                    } else {
                      formData.trigger(FORM_FIELDS.BUNKER_REGISTERED);
                    }
                  }}
                  defaultValue={bunkerBargeCheckbox}
                  disabled={isRestrictedToCertificatesUpdate || isBunkerBargeInProgress}
                />
              )}
            </div>
          </div>

          <div className="col-span-1 lg:col-span-3 h-0" />
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.GROSS_TONNAGE>
              testId="register-gross-tonnage-input"
              name={FORM_FIELDS.GROSS_TONNAGE}
              type="number"
              label={t("vessel-management.gross-tonnage")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NET_TONNAGE>
              testId="register-net-tonnage-input"
              name={FORM_FIELDS.NET_TONNAGE}
              type="number"
              label={t("vessel-management.net-tonnage")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DEADWEIGHT_TONNAGE>
              testId="register-deadweight-tonnage-input"
              name={FORM_FIELDS.DEADWEIGHT_TONNAGE}
              type="number"
              label={t("vessel-management.deadweight-tonnage")}
              decimals={3}
              maxDigits={11}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.LENGTH_OVERALL>
              testId="register-length-overall-input"
              name={FORM_FIELDS.LENGTH_OVERALL}
              type="number"
              label={t("vessel-management.length-overall")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.DRAFT_MAX>
              testId="register-draft-max-input"
              name={FORM_FIELDS.DRAFT_MAX}
              type="number"
              label={t("vessel-management.maxDraft")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
          <div className="col-span-1">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.BEAM>
              testId="register-beam-input"
              name={FORM_FIELDS.BEAM}
              type="number"
              label={t("vessel-management.beam")}
              decimals={3}
              maxDigits={15}
              onFieldChange={updateFormData}
              canBeginWithZero={false}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default VesselParticulars;
